// SPDX-License-Identifier: AGPL-3.0-only

import { defineStore } from "pinia";
import {
  assignTenantTier,
  createTenant,
  changeTenantStatus,
  getTenant,
  deleteTenant,
  renameTenant,
  listTenants,
  type CreateTenantInput,
  type TenantSummary,
  type TenantActivationStatus,
} from "../api/tenants.js";

export type TenantStoreStatus = "idle" | "loading" | "ready" | "failed";

interface TenantState {
  status: TenantStoreStatus;
  tenants: TenantSummary[];
  selectedTenantId: string | null;
  ownerUserId: string | null;
}

function storageKey(userId: string): string {
  return `lacunascope:selected-tenant:${userId}`;
}

function findTenant(tenants: readonly TenantSummary[], tenantId: string | null): TenantSummary | null {
  if (tenantId === null) {
    return null;
  }
  return tenants.find((tenant: TenantSummary): boolean => tenant.id === tenantId) ?? null;
}

function tenantStatusRank(tenant: TenantSummary): number {
  if (tenant.status === "active") return 0;
  return tenant.status === "suspended" ? 1 : 2;
}

function sortTenants(tenants: readonly TenantSummary[]): TenantSummary[] {
  return [...tenants].sort((left: TenantSummary, right: TenantSummary): number => {
    const statusDifference: number = tenantStatusRank(left) - tenantStatusRank(right);
    if (statusDifference !== 0) return statusDifference;
    const nameDifference: number = left.displayName.localeCompare(right.displayName);
    return nameDifference !== 0 ? nameDifference : left.id.localeCompare(right.id);
  });
}

export const useTenantStore = defineStore("tenants", {
  state: (): TenantState => ({
    status: "idle",
    tenants: [],
    selectedTenantId: null,
    ownerUserId: null,
  }),
  getters: {
    selectedTenant: (state: TenantState): TenantSummary | null =>
      findTenant(state.tenants, state.selectedTenantId),
  },
  actions: {
    async initialize(userId: string): Promise<void> {
      if (this.ownerUserId === userId && this.status !== "idle" && this.status !== "failed") {
        return;
      }

      this.status = "loading";
      this.ownerUserId = userId;
      try {
        const accessibleTenants: readonly TenantSummary[] = await listTenants();
        this.tenants = sortTenants(accessibleTenants);
        const storedTenantId: string | null = localStorage.getItem(storageKey(userId));
        const storedTenant: TenantSummary | null = findTenant(this.tenants, storedTenantId);
        this.selectedTenantId = storedTenant?.id ?? this.tenants[0]?.id ?? null;
        if (this.selectedTenantId === null) {
          localStorage.removeItem(storageKey(userId));
        } else {
          localStorage.setItem(storageKey(userId), this.selectedTenantId);
        }
        this.status = "ready";
      } catch (error: unknown) {
        this.tenants = [];
        this.selectedTenantId = null;
        this.status = "failed";
        throw error;
      }
    },
    selectTenant(tenantId: string, userId: string): void {
      if (this.ownerUserId !== userId || findTenant(this.tenants, tenantId) === null) {
        throw new Error("The selected tenant is not accessible to this user");
      }

      this.selectedTenantId = tenantId;
      localStorage.setItem(storageKey(userId), tenantId);
    },
    async addTenant(
      input: Omit<CreateTenantInput, "tierId">,
      tierId: string,
      userId: string,
    ): Promise<TenantSummary> {
      const createInput: CreateTenantInput = { ...input, tierId };
      const tenant: TenantSummary = await createTenant(createInput);
      this.tenants = sortTenants([
        ...this.tenants.filter((item: TenantSummary): boolean => item.id !== tenant.id),
        tenant,
      ]);
      this.ownerUserId = userId;
      this.status = "ready";
      this.selectTenant(tenant.id, userId);
      return tenant;
    },
    async removeTenant(tenantId: string, userId: string): Promise<void> {
      await deleteTenant(tenantId);
      this.tenants = this.tenants.filter((tenant: TenantSummary): boolean => tenant.id !== tenantId);
      if (this.selectedTenantId === tenantId) {
        this.selectedTenantId = this.tenants[0]?.id ?? null;
      }
      this.ownerUserId = userId;
      if (this.selectedTenantId === null) localStorage.removeItem(storageKey(userId));
      else localStorage.setItem(storageKey(userId), this.selectedTenantId);
    },
    async setTenantStatus(
      tenantId: string,
      status: TenantActivationStatus,
    ): Promise<TenantSummary> {
      const updatedTenant: TenantSummary = await changeTenantStatus(tenantId, status);
      this.tenants = sortTenants(this.tenants.map(
        (tenant: TenantSummary): TenantSummary =>
          tenant.id === updatedTenant.id ? updatedTenant : tenant,
      ));
      return updatedTenant;
    },
    async renameTenant(tenantId: string, displayName: string): Promise<TenantSummary> {
      const updatedTenant: TenantSummary = await renameTenant(tenantId, { displayName });
      this.tenants = sortTenants(this.tenants.map(
        (tenant: TenantSummary): TenantSummary =>
          tenant.id === updatedTenant.id ? updatedTenant : tenant,
      ));
      return updatedTenant;
    },
    async setTenantTier(tenantId: string, tierId: string): Promise<TenantSummary> {
      await assignTenantTier(tenantId, tierId);
      const updatedTenant: TenantSummary = await getTenant(tenantId);
      this.tenants = sortTenants(this.tenants.map(
        (tenant: TenantSummary): TenantSummary =>
          tenant.id === updatedTenant.id ? updatedTenant : tenant,
      ));
      return updatedTenant;
    },
    reset(): void {
      this.status = "idle";
      this.tenants = [];
      this.selectedTenantId = null;
      this.ownerUserId = null;
    },
  },
});
